import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { type AirActionTrackingLocation } from '@air/analytics';
import { ConfirmationModal } from '@air/component-confirmation-modal';
import { useAirModal } from '@air/provider-modal';
import { resetSelectedItemsAction } from '@air/redux-selected-items';

import { useAddAssetsToBoardsModal } from '~/components/Modals/AddAssetsToBoardsModal/useAddAssetsToBoardsModal';
import { useMoveItemsModal } from '~/components/Modals/MoveItemsModal/useMoveItemsModal';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useIsFlattenedView } from '~/hooks/search/useIsFlattenedView';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { usePrivateSelectAllTotalCount } from '~/hooks/usePrivateSelectAllTotalCount';
import { canRemoveAssetsFromBoard } from '~/utils/permissions/boardPermissions';

type BoardMembershipOperation = 'add' | 'move';

const getNestedAssetsConfirmationCopy = (operation: BoardMembershipOperation) =>
  operation === 'add'
    ? {
        title: 'Only assets will be added',
        description:
          "All selected assets from this board and its nested boards will be added to the destination you choose. The boards themselves won't be added.",
      }
    : {
        title: 'Only assets will be moved',
        description:
          "All selected assets from this board and its nested boards will be moved to the destination you choose. The boards themselves won't be moved.",
      };

export const usePrivateSelectAllBoardMembershipActions = () => {
  const dispatch = useDispatch();
  const selectAllRootBoardId = useViewsSelectAllStore((state) => state.selectAllRootBoardId);
  const includeNestedItems = useViewsSelectAllStore((state) => state.includeNestedItems);
  const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);
  const selectAllTotalCount = usePrivateSelectAllTotalCount();
  const { isFlattenedView } = useIsFlattenedView();
  const { getBoardPermissions } = useBoardPermissionsCache();
  const { showAddAssetsToBoardsModal } = useAddAssetsToBoardsModal();
  const { showMoveItemsModal } = useMoveItemsModal();
  const [showNestedAssetsConfirmation, hideNestedAssetsConfirmation] = useAirModal(ConfirmationModal);
  const includeDescendants = includeNestedItems || isFlattenedView;

  const sourceBoardPermissions = selectAllRootBoardId ? getBoardPermissions(selectAllRootBoardId) : undefined;
  const canRunBoardMembershipAction =
    !!selectAllRootBoardId && !!selectAllTotalCount && canRemoveAssetsFromBoard(sourceBoardPermissions);

  const clearSelection = useCallback(() => {
    resetSelectAll();
    dispatch(resetSelectedItemsAction());
  }, [dispatch, resetSelectAll]);

  const confirmNestedAssets = useCallback(
    (operation: BoardMembershipOperation, onConfirm: () => void) => {
      const copy = getNestedAssetsConfirmationCopy(operation);
      showNestedAssetsConfirmation({
        id: `select-all-${operation}-nested-assets-confirmation`,
        isOpen: true,
        title: copy.title,
        description: copy.description,
        ctaPrimaryText: 'Continue',
        onConfirm: () => {
          hideNestedAssetsConfirmation();
          onConfirm();
        },
        onDismiss: hideNestedAssetsConfirmation,
      });
    },
    [hideNestedAssetsConfirmation, showNestedAssetsConfirmation],
  );

  const openAddToBoardModal = useCallback(
    (trackLocation: AirActionTrackingLocation) => {
      if (!selectAllRootBoardId || !selectAllTotalCount) {
        return;
      }

      const openModal = () =>
        showAddAssetsToBoardsModal({
          onSubmit: clearSelection,
          parentBoardId: selectAllRootBoardId,
          selectAll: {
            boardId: selectAllRootBoardId,
            includeDescendants,
            totalSelectedItemsCount: selectAllTotalCount,
          },
          trackLocation,
        });

      if (includeDescendants) {
        confirmNestedAssets('add', openModal);
        return;
      }

      openModal();
    },
    [
      clearSelection,
      confirmNestedAssets,
      includeDescendants,
      selectAllRootBoardId,
      selectAllTotalCount,
      showAddAssetsToBoardsModal,
    ],
  );

  const openMoveItemsModal = useCallback(
    (trackLocation: AirActionTrackingLocation) => {
      if (!selectAllRootBoardId || !selectAllTotalCount) {
        return;
      }

      const openModal = () =>
        showMoveItemsModal({
          onMove: clearSelection,
          selectAll: {
            boardId: selectAllRootBoardId,
            includeDescendants,
            totalSelectedItemsCount: selectAllTotalCount,
          },
          trackLocation,
        });

      if (includeDescendants) {
        confirmNestedAssets('move', openModal);
        return;
      }

      openModal();
    },
    [
      clearSelection,
      confirmNestedAssets,
      includeDescendants,
      selectAllRootBoardId,
      selectAllTotalCount,
      showMoveItemsModal,
    ],
  );

  return {
    canRunBoardMembershipAction,
    openAddToBoardModal,
    openMoveItemsModal,
    selectAllTotalCount,
  };
};
